/** The current door, shown very large (`.dist`, `.adr`) on the guidance screen. */
import { Badge } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { PhantomBadge } from './PhantomBadge';
import { bearingDegrees, compassLabel, distanceMetres } from '../utils/geo';

import type { GeoPoint } from '../../../api/sessions';
import type { Assignment, Building } from '../../../api/types';

export interface DoorHeroProps {
  assignment: Assignment;
  /** The volunteer's live position, or the session's `start_point` as a fallback. */
  referencePoint: GeoPoint;
  /** The building of the current door, when already fetched — adds building-level badges. */
  building?: Building | undefined;
}

/** Count doors of a building whose origin makes them worth a second look. */
function countToVerify(building: Building): number {
  return building.floors.reduce(
    (total, floor) => total + floor.doors.filter((door) => door.kind !== 'real').length,
    0,
  );
}

/** Count non-voter electors across every door of a building. */
function countNonVoters(building: Building): number {
  return building.floors.reduce(
    (total, floor) => total + floor.doors.reduce((sum, door) => sum + door.non_voters, 0),
    0,
  );
}

/** Total doors in a building. */
function countDoors(building: Building): number {
  return building.floors.reduce((total, floor) => total + floor.doors.length, 0);
}

/**
 * Render the current door in large type: distance, direction, address and a
 * few badges — building size, non-voting households, doors worth a second
 * look.
 *
 * @param props - The pending assignment, a reference point, and optionally
 *   the building it belongs to (for the building-level badges).
 * @returns The door hero element.
 */
export function DoorHero({ assignment, referencePoint, building }: DoorHeroProps) {
  const { door } = assignment;
  const targetPoint = door.address.point;

  return (
    <div className="bloc">
      <div className="dist">
        {targetPoint ? (
          <>
            {t('session.card.distance', { distance: distanceMetres(referencePoint, targetPoint) })}
            <small>
              {t('session.guide.directionLabel', {
                direction: compassLabel(bearingDegrees(referencePoint, targetPoint)),
              })}
            </small>
          </>
        ) : (
          <small>{t('session.guide.noDistance')}</small>
        )}
      </div>
      <div className="adr">{door.address.label}</div>
      {door.building_name ? (
        <p className="muted" style={{ margin: '0 0 var(--space-3)' }}>
          {door.building_name}
        </p>
      ) : null}
      <div className="badges">
        {building ? (
          <>
            <Badge tone="violet">
              {t('session.guide.buildingBadge', { count: countDoors(building) })}
            </Badge>
            {countNonVoters(building) > 0 ? (
              <Badge tone="red">
                {t('session.guide.nonVotersBadge', { count: countNonVoters(building) })}
              </Badge>
            ) : null}
            {countToVerify(building) > 0 ? (
              <Badge tone="yellow">
                {t('session.guide.toVerifyBadge', { count: countToVerify(building) })}
              </Badge>
            ) : null}
          </>
        ) : (
          <>
            {door.is_canvass_target ? (
              <Badge tone="red">{t('session.guide.canvassTargetBadge')}</Badge>
            ) : null}
            {door.is_registration_target ? (
              <Badge tone="yellow">{t('session.guide.registrationTargetBadge')}</Badge>
            ) : null}
          </>
        )}
      </div>
      <PhantomBadge kind={door.kind} confidence={door.confidence} hypothesis={door.hypothesis} />
    </div>
  );
}
